/**
 * Message List Component
 * 
 * Displays conversation history with tool visualization and message formatting.
 * Handles different message types and provides smooth scrolling behavior.
 * Now supports markdown rendering using the marked library.
 */

import { useEffect, useRef } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { motion } from 'framer-motion';
import { Bot, User, Wrench } from 'lucide-react';
import { cn } from '@/lib/utils';
import { marked } from 'marked';
import { Message, HIDDEN_TOOLS } from './types';
import { ToolInvocation } from './ToolInvocation';

// Configure marked options for better markdown rendering
marked.setOptions({
  breaks: true,
  gfm: true
});

interface MessageListProps {
  messages: Message[];
  isLoading?: boolean;
  className?: string;
}

export function MessageList({ messages, isLoading = false, className }: MessageListProps) {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Add custom styles for markdown content
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .markdown-content h1, .markdown-content h2, .markdown-content h3, 
      .markdown-content h4, .markdown-content h5, .markdown-content h6 {
        font-weight: 600;
        margin-top: 1rem;
        margin-bottom: 0.5rem;
        line-height: 1.25;
      }
      .markdown-content h1 { font-size: 1.125rem; }
      .markdown-content h2 { font-size: 1rem; }
      .markdown-content h3, .markdown-content h4, .markdown-content h5, .markdown-content h6 { font-size: 0.875rem; }
      
      .markdown-content p {
        margin-bottom: 0.75rem;
        line-height: 1.6;
      }
      
      .markdown-content ul, .markdown-content ol {
        margin-bottom: 0.75rem;
        padding-left: 1.5rem;
      }
      
      .markdown-content li {
        margin-bottom: 0.25rem;
        line-height: 1.5;
      }
      
      .markdown-content code {
        background-color: hsl(var(--muted));
        padding: 0.125rem 0.25rem;
        border-radius: 0.25rem;
        font-size: 0.75rem;
        font-family: ui-monospace, SFMono-Regular, "SF Mono", Consolas, "Liberation Mono", Menlo, monospace;
      }
      
      .markdown-content pre {
        background-color: hsl(var(--muted));
        padding: 0.5rem;
        border-radius: 0.25rem;
        overflow-x: auto;
        margin-bottom: 0.75rem;
      }
      
      .markdown-content pre code {
        background: none;
        padding: 0;
        border-radius: 0;
        font-size: 0.75rem;
      }
      
      .markdown-content blockquote {
        border-left: 4px solid hsl(var(--muted));
        padding-left: 1rem;
        font-style: italic;
        color: hsl(var(--muted-foreground));
        margin-bottom: 0.75rem;
      }
      
      .markdown-content strong {
        font-weight: 600;
      }
      
      .markdown-content em {
        font-style: italic;
      }
      
      .markdown-content a {
        color: hsl(var(--primary));
        text-decoration: underline;
        text-decoration-color: hsl(var(--primary) / 0.3);
      }
      
      .markdown-content a:hover {
        text-decoration-color: hsl(var(--primary);
      }
      
      .dark .markdown-content code {
        background-color: hsl(var(--muted));
      }
      
      .dark .markdown-content pre {
        background-color: hsl(var(--muted));
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isLoading]);

  const getMessageIcon = (role: Message['role']) => {
    switch (role) {
      case 'user':
        return <User className="h-4 w-4" />;
      case 'assistant':
        return <Bot className="h-4 w-4" />;
      case 'tool':
        return <Wrench className="h-4 w-4" />;
      default:
        return <Bot className="h-4 w-4" />;
    }
  };

  const getMessageAlignment = (role: Message['role']) => {
    return role === 'user' ? 'justify-end' : 'justify-start';
  };

  const getMessageStyling = (role: Message['role']) => {
    switch (role) {
      case 'user':
        return 'bg-primary text-primary-foreground ml-12';
      case 'assistant':
        return 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 mr-12';
      case 'tool':
        return 'bg-muted/50 border border-border/50 mx-8';
      default:
        return 'bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 mr-12';
    }
  };

  const formatMessageContent = (content: string): { __html: string } | React.ReactElement[] => {
    try {
      // Use marked to render markdown content
      // marked v15+ returns a string synchronously
      const htmlContent = marked(content) as string;
      
      // Return HTML content for dangerouslySetInnerHTML
      return { __html: htmlContent };
    } catch (error) {
      console.error('Error parsing markdown:', error);
      // Fallback to plain text if markdown parsing fails
      return content.split('\n').map((line, idx, array) => (
        <span key={idx}>
          {line}
          {idx !== array.length - 1 && <br />}
        </span>
      ));
    }
  };

  return (
    <ScrollArea 
      ref={scrollAreaRef}
      className={cn("flex-1 bg-gradient-to-br from-slate-50 to-white dark:from-slate-900 dark:to-slate-800", className)}
    >
      <div className="p-4 space-y-4">
        {messages.map((message) => (
          <motion.div
            key={message.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className={cn("flex", getMessageAlignment(message.role))}
          >
            <div className={cn("max-w-[85%] rounded-lg shadow-sm", getMessageStyling(message.role))}>
              {/* Message header for non-user messages */}
              {message.role !== 'user' && (
                <div className="flex items-center gap-2 px-4 pt-3 pb-1">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {getMessageIcon(message.role)}
                    <span className="font-medium">
                      {message.role === 'assistant' ? 'Brand Consultant' : 
                       message.role === 'tool' ? 'Tool Execution' : 
                       'System'}
                    </span>
                    <span className="text-xs">
                      {new Date(message.timestamp).toLocaleTimeString()}
                    </span>
                  </div>
                  
                  {message.creditsUsed && (
                    <div className="ml-auto">
                      <span className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                        {message.creditsUsed} credits
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Message content */}
              <div className={cn(
                "px-4 py-3",
                message.role !== 'user' && "pt-1"
              )}>
                {/* Tool invocations (displayed before content for tool messages) */}
                {/* Note: Internal tools like 'respond_to_user' are filtered out using HIDDEN_TOOLS */}
                {(() => {
                  const visibleInvocations = message.toolInvocations?.filter(
                    invocation => !HIDDEN_TOOLS.includes(invocation.toolName as any)
                  ) || [];
                  
                  return visibleInvocations.length > 0 && (
                    <div className={cn(
                      "space-y-2",
                      message.content && "mb-3"
                    )}>
                      {visibleInvocations.map((invocation, invocationIndex) => (
                        <ToolInvocation
                          key={`${message.id}-tool-${invocationIndex}`}
                          invocation={invocation}
                        />
                      ))}
                    </div>
                  );
                })()}

                {/* Message text content */}
                {message.content && (
                  <div className={cn(
                    "text-sm leading-relaxed prose prose-sm max-w-none dark:prose-invert",
                    message.role === 'user' ? "text-primary-foreground" : "text-foreground"
                  )}>
                    {(() => {
                      const formattedContent = formatMessageContent(message.content);
                      if ('__html' in formattedContent) {
                        // Handle HTML content from marked
                        return (
                          <div 
                            dangerouslySetInnerHTML={formattedContent}
                            className="markdown-content"
                          />
                        );
                      }
                      // Handle fallback content
                      return formattedContent;
                    })()}
                  </div>
                )}
              </div>

              {/* User message timestamp */}
              {message.role === 'user' && (
                <div className="px-4 pb-2">
                  <div className="text-xs text-primary-foreground/70 text-right">
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        ))}

        {/* Loading indicator */}
        {isLoading && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-start"
          >
            <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-sm mr-12">
              <div className="flex items-center gap-2 px-4 pt-3 pb-1">
                <Bot className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">Brand Consultant</span>
              </div>
              
              <div className="px-4 py-3 pt-1">
                <motion.div 
                  className="flex items-center gap-2"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <motion.div
                    animate={{ y: [0, -4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                    className="w-2 h-2 bg-primary rounded-full"
                  />
                  <motion.div
                    animate={{ y: [0, -4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.2 }}
                    className="w-2 h-2 bg-primary rounded-full"
                  />
                  <motion.div
                    animate={{ y: [0, -4, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut", delay: 0.4 }}
                    className="w-2 h-2 bg-primary rounded-full"
                  />
                  <span className="text-sm text-muted-foreground ml-2">Thinking...</span>
                </motion.div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>
    </ScrollArea>
  );
}